import type { QuestKey } from "@/components/QuestsV2/types";
import { DiscordLinkedAccountPlatform } from "./discord";
import { SteamLinkedAccountPlatform } from "./steam";
import { XLinkedAccountPlatform } from "./x";

export const PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY = "pending_social_quest";

export interface ValidateLinkedAccountQuestResponse {
  following: boolean;
}

export interface LinkedAccountAuthResponse {
  access_token: string;
  refresh_token: string;
  external_user_id: string;
}

export interface LinkedAccountPlatform {
  imageUrl: string;
  name: string;
  questAction: string;
  getAuthorizeUrl: (loginOnly?: boolean) => string;
  login: (
    accessToken: string,
    params: string,
  ) => Promise<LinkedAccountAuthResponse>;
  validateQuest: (
    gameId: string,
    questId: string,
    accessToken: string,
  ) => Promise<ValidateLinkedAccountQuestResponse>;
  validateTweetQuest?: (
    gameId: string,
    questId: string,
    accessToken: string,
    startTime: string,
  ) => Promise<ValidateLinkedAccountQuestResponse>;
  validatePostTweetQuest?: (
    gameId: string,
    questId: string,
    accessToken: string,
    tweetText: string,
    imageId?: string,
  ) => Promise<ValidateLinkedAccountQuestResponse>;
  validateLikeAndRetweetQuest?: (
    gameId: string,
    questId: string,
    accessToken: string,
  ) => Promise<ValidateLinkedAccountQuestResponse>;
  uploadXImageFile?: (imageFile: File) => Promise<{ imageId: string }>;
  isLoggedIn: () => boolean;
}

export interface PendingSocialQuest {
  gameId: string;
  ruleId: string;
  questLink: string;
  gameName: string;
  questKey: QuestKey;
  twitterHandle?: string;
  /**
   * If true, this will only link the social account without performing follow or quest validation.
   * Used when we only need to connect a social account without completing a quest.
   */
  loginOnly?: boolean;
  tweetText?: string;
  tweetImageId?: string;
  linkUrl?: string;
}

// Note: If you are looking for the telegram platform, it is not included here. As the behavior is different from the other platforms.
// It is handled separately in the TelegramQuestModal.tsx file.
export const linkedAccountPlatforms: Map<QuestKey, LinkedAccountPlatform> =
  new Map([
    ["discord-v2", DiscordLinkedAccountPlatform],
    ["x-v2", XLinkedAccountPlatform],
    ["x-share-validation", XLinkedAccountPlatform],
    ["x-post-tweet-validation", XLinkedAccountPlatform],
    ["x-like-and-retweet-validation", XLinkedAccountPlatform],
    ["steam", SteamLinkedAccountPlatform],
  ]);

export function openAuthorizeUrl(
  ruleId: string,
  key: QuestKey,
  questLink: string,
  gameName: string,
  gameId: string,
  loginOnly?: boolean,
  tweetText?: string,
  twitterHandle?: string,
  tweetImageId?: string,
  linkUrl?: string,
) {
  const platform = linkedAccountPlatforms.get(key);
  if (!platform) {
    return;
  }

  const authorizeUrl = platform.getAuthorizeUrl(loginOnly);
  const pendingSocialQuest: PendingSocialQuest = {
    gameId,
    ruleId,
    questLink,
    gameName,
    questKey: key,
    twitterHandle,
    loginOnly,
    tweetText,
    tweetImageId,
    linkUrl,
  };
  localStorage.setItem(
    PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY,
    JSON.stringify(pendingSocialQuest),
  );
  window.open(authorizeUrl, "_blank");
}

export async function validateQuest(
  gameId: string,
  key: QuestKey,
  ruleId: string,
  accessToken: string,
  startTime: string,
) {
  const platform = linkedAccountPlatforms.get(key);
  if (!platform) {
    throw new Error("Invalid quest key");
  }

  if (key === "x-share-validation") {
    if (!platform.validateTweetQuest) {
      throw new Error("Platform does not support tweet validation");
    }
    // add buffer to ensure recent tweet posted is available for validation
    await new Promise((resolve) => setTimeout(resolve, 8000));
    return (
      await platform.validateTweetQuest(gameId, ruleId, accessToken, startTime)
    ).following;
  }

  return (await platform.validateQuest(gameId, ruleId, accessToken)).following;
}
